/**
 * Player settings: persisted per device, merged over DEFAULT_SETTINGS.
 *
 * Only the choices a player actually made are stored (the difference from the
 * environment defaults). So `reducedMotion` follows the OS
 * `prefers-reduced-motion` setting — and `locale` follows the browser language —
 * until the player overrides it, and a future change to a default reaches
 * everyone who never touched that setting.
 *
 * Applying settings (i18n locale, audio volumes, CSS classes) is the UI's job:
 * this module only stores them and notifies subscribers.
 */
import { useSyncExternalStore } from 'react';
import type { Locale } from '@dropstack/core';
import { DEFAULT_SETTINGS, type AudioSettings, type InputPreference, type Settings } from '../contracts.ts';
import { createObservable, type Observable } from './observable.ts';
import { getAppStorage, isFiniteNumber, isRecord, STORAGE_KEYS, type JsonStore } from './storage.ts';

/** What the device tells us before the player has chosen anything. */
export interface SettingsEnvironment {
  prefersReducedMotion(): boolean;
  /** Preferred UI language from the browser, or null when neither English nor Afrikaans is preferred. */
  preferredLocale(): Locale | null;
}

/** Reads matchMedia / navigator.languages, guarded for non-browser runtimes. */
export const browserEnvironment: SettingsEnvironment = {
  prefersReducedMotion() {
    try {
      return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  },
  preferredLocale() {
    try {
      if (typeof navigator === 'undefined') return null;
      const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
      for (const lang of langs) {
        const base = lang?.toLowerCase().split('-')[0];
        if (base === 'af' || base === 'en') return base;
      }
      return null;
    } catch {
      return null;
    }
  },
};

/** DEFAULT_SETTINGS adjusted for the environment: the value of every setting the player hasn't chosen. */
export function environmentDefaults(env: SettingsEnvironment = browserEnvironment): Settings {
  return {
    ...DEFAULT_SETTINGS,
    audio: { ...DEFAULT_SETTINGS.audio },
    locale: env.preferredLocale() ?? DEFAULT_SETTINGS.locale,
    reducedMotion: env.prefersReducedMotion(),
  };
}

/** Load settings: environment defaults with the player's saved choices on top. Never throws. */
export function loadSettings(store: JsonStore = getAppStorage(), env: SettingsEnvironment = browserEnvironment): Settings {
  const base = environmentDefaults(env);
  const saved = store.get(STORAGE_KEYS.settings, decodeSavedSettings);
  return saved ? mergeSettings(base, saved) : base;
}

/**
 * Persist settings. Stores only the values that differ from the environment
 * defaults, so unchanged settings keep following the device. Returns false if
 * storage is unavailable (the settings still apply for this session).
 */
export function saveSettings(
  settings: Settings,
  store: JsonStore = getAppStorage(),
  env: SettingsEnvironment = browserEnvironment,
): boolean {
  const diff = diffSettings(environmentDefaults(env), settings);
  if (Object.keys(diff).length === 0) {
    store.remove(STORAGE_KEYS.settings);
    return true;
  }
  return store.set(STORAGE_KEYS.settings, diff);
}

/** Apply a patch (audio is merged field by field). Returns a new object. */
export function mergeSettings(current: Settings, patch: SettingsPatch): Settings {
  const { audio, ...rest } = patch;
  return { ...current, ...rest, audio: audio ? { ...current.audio, ...audio } : current.audio };
}

/** A settings change; `audio` may be partial ({ muted: true }). */
export type SettingsPatch = Partial<Omit<Settings, 'audio'>> & { audio?: Partial<AudioSettings> };

// ───────────── Store + hook ─────────────

export interface SettingsStore extends Observable<Settings> {
  /** Merge a patch, persist it and notify subscribers. */
  update(patch: SettingsPatch): void;
  /** Forget every saved choice and return to the environment defaults. */
  reset(): void;
}

export function createSettingsStore(
  store: JsonStore = getAppStorage(),
  env: SettingsEnvironment = browserEnvironment,
): SettingsStore {
  const value = createObservable(loadSettings(store, env));
  return {
    get: value.get,
    subscribe: value.subscribe,
    update(patch) {
      const next = mergeSettings(value.get(), patch);
      saveSettings(next, store, env);
      value.set(next);
    },
    reset() {
      store.remove(STORAGE_KEYS.settings);
      value.set(environmentDefaults(env));
    },
  };
}

let appSettings: SettingsStore | null = null;

/** The app-wide settings store (localStorage-backed), created on first use. */
export function getSettingsStore(): SettingsStore {
  appSettings ??= createSettingsStore();
  return appSettings;
}

/**
 * React hook: current settings and an updater that merges a patch, persists
 * it and re-renders every subscriber.
 *
 *   const [settings, updateSettings] = useSettings();
 *   updateSettings({ locale: 'af' });
 */
export function useSettings(store: SettingsStore = getSettingsStore()): [Settings, (patch: SettingsPatch) => void] {
  const settings = useSyncExternalStore(store.subscribe, store.get, store.get);
  return [settings, store.update];
}

// ───────────── Decoding / diffing ─────────────

const INPUT_PREFS: readonly InputPreference[] = ['auto', 'mc', 'hw'];
const BOOLEAN_KEYS = [
  'reducedMotion',
  'largeText',
  'highContrast',
  'leftHanded',
  'relaxedPace',
  'leaderboardOptIn',
  'watchPreview',
  'handwritingCalibrated',
] as const satisfies readonly (keyof Settings)[];

/**
 * Validate saved JSON field by field: anything unknown or of the wrong type is
 * dropped (falls back to the default) rather than rejecting everything.
 */
function decodeSavedSettings(raw: unknown): SettingsPatch | null {
  if (!isRecord(raw)) return null;
  const out: SettingsPatch = {};
  if (raw.locale === 'en' || raw.locale === 'af') out.locale = raw.locale;
  const input = raw.input;
  if (typeof input === 'string') {
    const pref = INPUT_PREFS.find((p) => p === input);
    if (pref) out.input = pref;
  }
  for (const key of BOOLEAN_KEYS) {
    const v = raw[key];
    if (typeof v === 'boolean') out[key] = v;
  }
  if (isRecord(raw.audio)) {
    const audio: Partial<AudioSettings> = {};
    for (const key of ['master', 'music', 'sfx'] as const) {
      const v = raw.audio[key];
      if (isFiniteNumber(v)) audio[key] = Math.min(1, Math.max(0, v));
    }
    if (typeof raw.audio.muted === 'boolean') audio.muted = raw.audio.muted;
    if (Object.keys(audio).length > 0) out.audio = audio;
  }
  return out;
}

function diffSettings(base: Settings, current: Settings): SettingsPatch {
  const diff: SettingsPatch = changed(base, current, ['locale', 'input', ...BOOLEAN_KEYS]);
  const audio = changed(base.audio, current.audio, ['master', 'music', 'sfx', 'muted']);
  if (Object.keys(audio).length > 0) diff.audio = audio;
  return diff;
}

/** The subset of `keys` whose values differ between `base` and `current`. */
function changed<T extends object, K extends keyof T>(base: T, current: T, keys: readonly K[]): Partial<Pick<T, K>> {
  const out: Partial<Pick<T, K>> = {};
  for (const key of keys) if (current[key] !== base[key]) out[key] = current[key];
  return out;
}
